import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18n } from '@/i18n/i18n-provider';
import { filesApi } from '@/services/files.api';

/**
 * Any record's logo — a `FileObject` id, never a URL, same posture `Avatar`
 * takes for a person's photo. Square, not round, and no initials fallback:
 * a church, denomination or council logo is rarely a face crop.
 */
export function EntityLogo({
  logoFileId,
  size = 96,
}: {
  readonly logoFileId: string | null;
  readonly size?: number;
}): ReactNode {
  const { t } = useI18n();
  const download = useQuery({
    queryKey: ['files', 'download-url', logoFileId],
    queryFn: () => filesApi.getDownloadUrl(logoFileId!),
    enabled: logoFileId !== null,
    staleTime: 60_000,
  });

  if (download.data) {
    return (
      <img
        src={download.data.url}
        alt=""
        className="logo-preview"
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span className="logo-placeholder" style={{ width: size, height: size }} aria-hidden="true">
      {t('common.labels.noLogo')}
    </span>
  );
}
